import { ChevronRight } from 'ui/icons';
import SelectRadio from '@/components/shared/SelectRadio';
import { Layout } from '@/components/Common/Layout';
import useClaimCreateStore, {
  ClaimCreateFormSchema,
  EClaimCreateFlowSteps,
} from '@/store/claimCreateStore';
import {
  EBenefitCategory,
  EClaimType,
  EEnrollmentStatus,
  IBenefit,
  IMember,
} from '@/types';
import toast from 'react-hot-toast';
import { z } from 'zod';
import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { memberDetailsQuery } from '@/queries/memberDetails.queries';
import { getDimensions, redirectToPlumApp, track } from '@/utils';
import React, { useEffect, useState } from 'react';
import { useAppContext } from '@/context/AppContext';
import { MOBILE_DEEPLINKS, TRACKER_EVENTS } from '@/constants';
import { IClaimCreateStepProps } from '@/pages/create';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppConfig from '@/AppConfig';
import { getAvailableClaimTypes } from '@/queries/claims.queries';
import { Spinner } from 'ui/mobile-ds';

const Schema = ClaimCreateFormSchema.pick({
  typeOfClaim: true,
  benefitCategory: true,
});

type TSchema = z.infer<typeof Schema>;

const generateBenefitsMap = (
  members: Array<IMember>,
): Map<string, IBenefit> => {
  const benefitsMap = new Map<string, IBenefit>();
  members.forEach((member) => {
    member.benefits.forEach((benefit) => {
      benefitsMap.set(benefit.orgBenefitId, benefit);
    });
  });
  return benefitsMap;
};

const SelectClaimType = ({ startTimer }: IClaimCreateStepProps) => {
  const setFormState = useClaimCreateStore((state) => state.setFormState);
  const clearFormState = useClaimCreateStore((state) => state.clearFormState);
  const setCurrentStep = useClaimCreateStore((state) => state.setCurrentStep);
  const setMemberDetails = useClaimCreateStore(
    (state) => state.setMemberDetails,
  );
  const setAllBenefits = useClaimCreateStore((state) => state.setAllBenefits);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    // formState: { isValid },
  } = useForm<TSchema>({
    resolver: zodResolver(Schema),
  });

  const formRef = React.useRef<HTMLFormElement>(null);

  const watchTypeOfClaim = watch('typeOfClaim');
  const watchBenefitType = watch('benefitCategory') as EBenefitCategory;

  const { refetch: refetchMemberDetails, isFetching: isFetchingMemberDetails } =
    useQuery({
      initialData: null,
      queryKey: memberDetailsQuery(
        watchTypeOfClaim,
        watchTypeOfClaim === EClaimType.REIMBURSEMENT ? [watchBenefitType] : [],
      ).queryKey,
      queryFn: memberDetailsQuery(
        watchTypeOfClaim,
        watchTypeOfClaim === EClaimType.REIMBURSEMENT ? [watchBenefitType] : [],
      ).queryFn,
      enabled: false,
    });

  const { data: availableClaimTypes, isFetching: isFetchingClaimTypes } =
    useQuery({
      queryKey: getAvailableClaimTypes().queryKey,
      queryFn: getAvailableClaimTypes().queryFn,
    });

  const [loading, setLoading] = useState(false);

  const { isWebView, userConfig } = useAppContext();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (
      (availableClaimTypes?.length ?? 0) > 0 &&
      searchParams.has('typeOfClaim')
    ) {
      const availableClaimType = availableClaimTypes?.find(
        (type) => type.typeOfClaim === searchParams.get('typeOfClaim'),
      );

      if (availableClaimType) {
        let typeOfClaim: EClaimType | null = null;
        const benefitCategory: EBenefitCategory | null = searchParams.get(
          'benefitCategory',
        ) as EBenefitCategory;
        if (
          searchParams.get('typeOfClaim') === EClaimType.CASHLESS ||
          searchParams.get('typeOfClaim') === EClaimType.PLANNED
        ) {
          typeOfClaim = EClaimType.CASHLESS;
        } else if (
          searchParams.get('typeOfClaim') === EClaimType.REIMBURSEMENT
        ) {
          typeOfClaim = EClaimType.REIMBURSEMENT;
        }
        if (typeOfClaim) {
          searchParams.delete('typeOfClaim');
          searchParams.delete('benefitCategory');
          setSearchParams(searchParams, { replace: true });
          setValue('typeOfClaim', typeOfClaim);
          if (benefitCategory) {
            setValue('benefitCategory', benefitCategory);
          } else {
            setValue(
              'benefitCategory',
              availableClaimType?.benefitCategory ?? '',
            );
          }
          // Submitting after 500ms, as refetchMemberDetails doesn't return the correct data if submitted immediately
          // seems to be a bug in react-query
          setTimeout(() => {
            formRef.current?.dispatchEvent(
              new Event('submit', {
                cancelable: true,
                bubbles: true,
              }),
            );
          }, 500);
        }
      }
    }
  }, [availableClaimTypes, searchParams, setSearchParams, setValue]);

  const trackSubmit = ({
    typeOfClaim,
    benefitCategory,
  }: {
    typeOfClaim: EClaimType;
    benefitCategory: string;
  }) => {
    const viewSource = searchParams.get('utm_source');
    track({
      event: TRACKER_EVENTS.CC_CLAIM_CREATION_START,
      data: {
        viewSource,
        typeOfClaim,
        benefitCategory,
      },
    });
  };

  const onSubmit: SubmitHandler<TSchema> = async ({
    typeOfClaim,
    benefitCategory,
  }) => {
    if (
      typeOfClaim === EClaimType.REIMBURSEMENT &&
      benefitCategory !== EBenefitCategory.EXPENSE_MANAGEMENT
    ) {
      setLoading(true);
      if (isWebView) {
        window?.NativeBridge?.callDeeplink?.(
          MOBILE_DEEPLINKS.CREATE_REIMBURSEMENT_CLAIM,
        );
      } else {
        redirectToPlumApp('/claims/create?typeOfClaim=Reimbursement')();
      }
      return;
    }
    setFormState({ typeOfClaim, benefitCategory });
    const showErrorToast = () => {
      toast.error('Oops! Something went wrong. Please try again later.', {
        id: 'create-claim-error',
        style: { marginBottom: getDimensions('claims-footer').height },
      });
    };

    const status = userConfig?.enrollmentStatus;
    const pendingEnrollment =
      status &&
      [EEnrollmentStatus.INCOMPLETE, EEnrollmentStatus.ENROLLED].includes(
        status,
      );

    // if enrollment is pending; show org under enrollment screen
    if (pendingEnrollment) {
      // for Cashless, no need to refetch member details
      if (
        typeOfClaim === EClaimType.CASHLESS ||
        typeOfClaim === EClaimType.PLANNED
      ) {
        setCurrentStep(EClaimCreateFlowSteps.ORG_UNDER_ENROLLMENT);
        return;
      }

      const { data, isError } = await refetchMemberDetails();
      if (isError || !data) {
        showErrorToast();
      }
      setCurrentStep(EClaimCreateFlowSteps.ORG_UNDER_ENROLLMENT);
      setMemberDetails(data ?? []);
      return;
    }

    const { data, isError } = await refetchMemberDetails();
    if (isError || !data) {
      showErrorToast();
      return;
    }

    trackSubmit({ typeOfClaim, benefitCategory });
    startTimer?.();
    setMemberDetails(data);
    setAllBenefits(generateBenefitsMap(data));

    if (benefitCategory === EBenefitCategory.EXPENSE_MANAGEMENT) {
      setCurrentStep(EClaimCreateFlowSteps.TREATMENT_DETAILS);
    } else {
      setCurrentStep(EClaimCreateFlowSteps.SELECT_PATIENT);
    }
  };

  useEffect(() => {
    clearFormState();
    setLoading(false);
  }, []);

  const navigate = useNavigate();

  return (
    <Layout.Main
      loading={isFetchingClaimTypes}
      headerProps={{
        title: 'Pick a claim type',
        onBack: () => {
          navigate(AppConfig.HOME_PAGE);
        },
      }}
    >
      {({ getDimensions }) => {
        const onError: SubmitErrorHandler<TSchema> = (errors) => {
          window.dev.e(errors);
          toast.error('Please select a claim type', {
            id: 'create-claim-error',
            style: {
              marginBottom: getDimensions('claims-footer').height,
            },
          });
        };
        return (
          <form
            onSubmit={handleSubmit(onSubmit, onError)}
            className="flex flex-1 flex-col h-full mt-3 pb-24"
            ref={formRef}
          >
            <div className="flex flex-1 flex-col h-full gap-4 md:gap-5">
              {availableClaimTypes?.map((type) => {
                const isSelected =
                  watchTypeOfClaim === type.typeOfClaim &&
                  watchBenefitType === type.benefitCategory;

                return (
                  <SelectRadio
                    key={`${type.typeOfClaim}-${type.benefitCategory}`}
                    title={type.title}
                    description={type.subtitle}
                    icon={<img className="w-16 h-16" src={type.iconUrl} />}
                    id={`${type.typeOfClaim}-${type.benefitCategory}`}
                    value={`${type.typeOfClaim}-${type.benefitCategory}`}
                    selected={isSelected}
                    radioIcon={() => {
                      if (isSelected) {
                        return <Spinner />;
                      }
                      return <ChevronRight />;
                    }}
                    disabled={isFetchingMemberDetails || loading}
                    {...register('typeOfClaim', {
                      required: 'Please select a patient',
                    })}
                    // kept it outside the register to avoid overriding the value by the last value of Array.map()
                    onChange={() => {
                      setValue('benefitCategory', type.benefitCategory);
                      setValue('typeOfClaim', type.typeOfClaim);
                      formRef.current?.dispatchEvent(
                        new Event('submit', {
                          cancelable: true,
                          bubbles: true,
                        }),
                      );
                    }}
                  />
                );
              })}
            </div>
          </form>
        );
      }}
    </Layout.Main>
  );
};

export default SelectClaimType;
